/**
 * Chef-facing Goods Receipting status labels.
 * Stored values stay the same; this is display-only.
 */

/** Supplier confirmation status on a PO — stored enum stays Received|Confirmed|Changes. */
export function getOrderStatusDisplay(status) {
  const raw = String(status || '').trim()
  if (raw === 'Received') return 'Received by supplier'
  return raw
}

const NEUTRAL = {
  color: undefined,
  style: { color: '#000', background: '#fafafa', borderColor: '#d9d9d9' },
}

const SUCCESS = { color: 'green', style: {} }
const ATTENTION = { color: 'orange', style: {} }
const DANGER = { color: 'red', style: {} }
const EDITED = { color: 'purple', style: {} }

/** A receipt has not been saved, so the received total is still the order total. */
export function isReceiptNotSaved(receiptingStatus) {
  const raw = String(receiptingStatus || '').trim()
  return !raw || raw === 'Not Saved'
}
export const RETRO_EDITED_BADGE = {
  badge: 'Retrospectively edited',
  badgeTag: EDITED,
  tooltip:
    'This completed receipt was later corrected by an Admin / Head Chef GM. The audit trail keeps the original values.',
}

/**
 * Merge the permanent "Retrospectively edited" marker into any status
 * display. The primary status badge (e.g. Credit requested) is preserved;
 * the marker is appended so history is never hidden.
 */
export function withRetroEditedBadge(display, retrospectivelyEdited) {
  if (!retrospectivelyEdited || !display) return display
  const badges = []
  if (display.badge) badges.push(display.badge)
  badges.push(RETRO_EDITED_BADGE.badge)
  return {
    ...display,
    badge: badges.join(' · '),
    badgeTag: RETRO_EDITED_BADGE.badgeTag,
    tooltip: `${display.tooltip} ${RETRO_EDITED_BADGE.tooltip}`,
  }
}

/**
 * @returns {{
 *   primaryLabel: string,
 *   badge: string|null,
 *   primaryTag: { color?: string, style: object },
 *   badgeTag: { color?: string, style: object }|null,
 *   tooltip: string,
 *   rawStatus: string,
 *   isReceipted: boolean,
 * }}
 */
export function getGoodsReceiptingStatusDisplay(
  receiptingStatus,
  creditMeta = null,
) {
  const raw = String(receiptingStatus || '').trim() || 'Not Saved'

  switch (raw) {
    case 'Not Saved':
      return {
        primaryLabel: 'Not receipted',
        badge: null,
        primaryTag: NEUTRAL,
        badgeTag: null,
        tooltip:
          'Not receipted yet — Accept as ordered, or Save receipt after checking the delivery.',
        rawStatus: raw,
        isReceipted: false,
      }

    case 'Partially Receipted':
      return {
        primaryLabel: 'Partially receipted',
        badge: null,
        primaryTag: ATTENTION,
        badgeTag: null,
        tooltip:
          'Some lines on this PO are receipted; others are still outstanding. Finish remaining deliveries when they arrive.',
        rawStatus: raw,
        isReceipted: false,
      }

    case 'Sent to supplier':
    case 'CN Requested':
      return {
        primaryLabel: 'Receipted',
        badge: 'Credit requested',
        primaryTag: SUCCESS,
        badgeTag: ATTENTION,
        tooltip:
          'Order is receipted. Waiting on the supplier for the credit request — this is not the same as “not receipted”.',
        rawStatus: raw,
        isReceipted: true,
      }

    case 'Rejected by supplier':
      return {
        primaryLabel: 'Receipted',
        badge: 'Credit rejected',
        primaryTag: SUCCESS,
        badgeTag: DANGER,
        tooltip:
          'Order is receipted. The supplier rejected the credit request.',
        rawStatus: raw,
        isReceipted: true,
      }

    case 'Full accept':
      return {
        primaryLabel: 'Receipted',
        badge: null,
        primaryTag: SUCCESS,
        badgeTag: null,
        tooltip: 'Order receipted as ordered (Full accept).',
        rawStatus: raw,
        isReceipted: true,
      }

    case 'Saved':
      return {
        primaryLabel: 'Receipted',
        badge: null,
        primaryTag: SUCCESS,
        badgeTag: null,
        tooltip: 'Order receipted with line changes saved.',
        rawStatus: raw,
        isReceipted: true,
      }

    case 'Awaiting invoice reconciliation':
      return {
        primaryLabel: 'Receipted',
        badge: 'Awaiting invoice reconciliation',
        primaryTag: SUCCESS,
        badgeTag: ATTENTION,
        tooltip:
          'Order is receipted with a recorded discrepancy (no credit requested). The discrepancy is reviewed during invoice reconciliation.',
        rawStatus: raw,
        isReceipted: true,
      }

    case 'Credit approved by supplier': {
      // Ticket guard: "Credit approved" must never appear unless an
      // associated credit record is genuinely approved. Legacy/unknown
      // server values that fall into this status without evidence are
      // downgraded to the reconciliation-pending badge.
      const creditEvidence =
        creditMeta?.cnRequestStatus === 'approved' ||
        creditMeta?.editType === 'credit_note_request'
      if (!creditEvidence) {
        return {
          primaryLabel: 'Receipted',
          badge: 'Awaiting invoice reconciliation',
          primaryTag: SUCCESS,
          badgeTag: ATTENTION,
          tooltip:
            'Order is receipted. No approved credit record exists for this PO — the recorded discrepancy is reviewed during invoice reconciliation.',
          rawStatus: raw,
          isReceipted: true,
        }
      }
      return {
        primaryLabel: 'Receipted',
        badge: 'Credit approved',
        primaryTag: SUCCESS,
        badgeTag: SUCCESS,
        tooltip: 'Order is receipted. Supplier approved the credit.',
        rawStatus: raw,
        isReceipted: true,
      }
    }

    default:
      return {
        primaryLabel: raw,
        badge: null,
        primaryTag: ATTENTION,
        badgeTag: null,
        tooltip: raw,
        rawStatus: raw,
        isReceipted: false,
      }
  }
}
